import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import * as Yup from "yup";

import {
  CGSaleLandAndBuildingAddressForm,
  CGSaleLandAndBuildingBuyerForm,
  CGSaleLandAndBuildingInfoForm
} from "..";
import { ISaleLandAndBuildingForm } from "../../../../config/interfaces";
import {
  SaleLandAndBuildingFormSchema,
  SaleLandAndBuildingInitValues
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";

type Props = {
	showCanvas: boolean;
	formValues: ISaleLandAndBuildingForm | null;
	handleCloseBtnPress: (SaleLandAndBuildingData: ISaleLandAndBuildingForm | null) => void;
};

const CGSaleLandAndBuildingDetailsForm: React.FC<Props> = (props: Props) => {
	const { showCanvas, handleCloseBtnPress, formValues } = props;

	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const [initFormValues, setInitFormValues] = useState<ISaleLandAndBuildingForm>(
		SaleLandAndBuildingInitValues
	);

	useEffect(() => {
		if (showCanvas && formValues) {
			setInitFormValues(() => cloneDeep(formValues));
		} else {
			setInitFormValues(() => cloneDeep(SaleLandAndBuildingInitValues));
		}
	}, [showCanvas, formValues]);

	const checkShortOrLongTerm = async (values: ISaleLandAndBuildingForm, setFieldValue: any) => {
		const holdingPeriod = UtilService.calculateDateDifference(values.PurchaseDate, values.SaleDate);
		let _SplRateTax = 0;
		let _NrlRateIncome = 0;

		if (holdingPeriod < 365 * 2) {
			// Less than 2 year, Short Term Gains at Normal/Slab rate
			_NrlRateIncome = Number(values.CapitalGains);
		} else {
			// More than 2 year, Long Term Gains at 20%
			_SplRateTax = Number(values.CapitalGains) * 0.2;
		}

		setFieldValue("SplRateTax", _SplRateTax);
		setFieldValue("NrlRateIncome", _NrlRateIncome);

		// const _dateDifference = UtilService.calculateDateDifference(
		//   values.PurchaseDate,
		//   values.SaleDate
		// );
		// if (_dateDifference) {
		//   // for two years
		//   if (_dateDifference < 365 * 2) {
		//     const _netTaxValue = (Number(values.CapitalGains) * 20) / 100;
		//     setFieldValue("NetTax", _netTaxValue);
		//   }
		// }
	};

	return (
		<>
			<Offcanvas show={showCanvas} placement="end" backdrop={true}>
				<Formik
					enableReinitialize={true}
					initialValues={initFormValues}
					validationSchema={Yup.object().shape({ ...SaleLandAndBuildingFormSchema })}
					onSubmit={(values) => {
						let _values = { ...values };
						handleCloseBtnPress(_values);
					}}
				>
					{({ handleSubmit, setFieldValue, submitCount, isValid, values, errors }) => (
						<>
							<ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

							<Offcanvas.Header>
								<Offcanvas.Title>Add/Edit Sale of Land and Building Details</Offcanvas.Title>
								<Button
									variant="primary"
									className="icon-only hide-offcanvas"
									onClick={() => handleCloseBtnPress(null)}
								>
									<FASvgIcon icon={faXmark} />
								</Button>
							</Offcanvas.Header>

							<Offcanvas.Body>
								<CGSaleLandAndBuildingInfoForm />
								<CGSaleLandAndBuildingAddressForm />
								<CGSaleLandAndBuildingBuyerForm />
							</Offcanvas.Body>

							<div className="offcanvas-btns">
								{!isFilingReadOnly ? (
									<Button
										variant="outline-white"
										size="sm"
										onClick={() => {
											let sum = values.BuyerDetails.slice(1).reduce(
												(acc, item) => acc + (parseInt(item.Percentage) || 0),
												0
											);
											const firstBuyerPercentage = 100 - sum;
											if (sum > 100 || firstBuyerPercentage === 0) return;
											checkShortOrLongTerm(values, setFieldValue);
											handleSubmit();
										}}
									>
										Save and Close
									</Button>
								) : (
									<Button
										variant="outline-white"
										size="sm"
										onClick={() => handleCloseBtnPress(null)}
									>
										Close
									</Button>
								)}
							</div>
						</>
					)}
				</Formik>
			</Offcanvas>
		</>
	);
};

export default CGSaleLandAndBuildingDetailsForm;
